import { FormControl, MenuItem, Pagination, Select, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiDocumentText } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { filterParamsNotNull } from 'src/app/helpers/utils';
import {
  useGetMyApplicationsQuery,
  useGetTemplateCategoriesQuery,
  useLazyGetTemplateSubCategoriesByIdQuery,
} from 'src/app/services/claim/claimApi';
import ApprovalWorkflowCard from '../Components/Card';
import { CardsSkeleton } from '../Components/CardsSkeleton';
import Filters from '../Components/Filters';

function OutgoingDocumentPage() {
  const { t } = useTranslation('translations');
  const navigate = useNavigate();
  const [search, setSearch] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [status, setStatus] = useState(null);
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const { data: categoryOptions } = useGetTemplateCategoriesQuery();

  const [getSubcategories, { data: subcategoryOptions }] =
    useLazyGetTemplateSubCategoriesByIdQuery();

  useEffect(() => {
    setSubcategory(null);
    if (!category) return;
    getSubcategories({ categoryId: category });
  }, [category, getSubcategories]);

  useEffect(() => {
    setPage(1);
  }, [search, status, category, subcategory, startDate, endDate, limit]);

  const { data: applications, isLoading } = useGetMyApplicationsQuery(
    filterParamsNotNull({
      search,
      status,
      category,
      subcategory,
      end: endDate,
      start: startDate,
      page,
      limit,
    })
  );

  const totalCount =
    applications?.totalCount ?? applications?.totalElements ?? applications?.total ?? 0;

  const pageCount = useMemo(() => {
    if (!totalCount || !limit) return 1;
    return Math.max(1, Math.ceil(totalCount / limit));
  }, [totalCount, limit]);

  return (
    <div className="px-12 py-8" style={{ overflowY: 'hidden' }}>
      <Typography variant="h5" px={2} py={1} display="flex" gap={0.5}>
        <HiDocumentText className="text-grey-800 dark:text-grey-500" />
        <span>{t('OutgoingDocuments')}</span>
      </Typography>

      <Filters
        search={search}
        setSearch={setSearch}
        status={status}
        setStatus={setStatus}
        category={category}
        setCategory={setCategory}
        subcategory={subcategory}
        setSubcategory={setSubcategory}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        categories={categoryOptions}
        subcategories={subcategoryOptions}
      />

      <Box
        sx={{
          minHeight: '68vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          my: 2,
        }}
      >
        {isLoading ? (
          <CardsSkeleton count={limit} />
        ) : applications?.items?.length ? (
          applications.items.map((el) => (
            <ApprovalWorkflowCard
              id={el.id}
              onClick={() => navigate(`/apps/documents/my-applications/view/${el.id}`)}
              key={el.id ?? `${el.name}-${el.createDate}`}
              title={el.name}
              category={el.category}
              author={el.creatorName?.name}
              description={el.templateName}
              date={el.createDate || el.createdDate}
              statusChip={el.status}
              approvers={el.approvers}
              deptChip="-"
              progress={50}
              statusId={el.statusId}
            />
          ))
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: '40vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            <HiDocumentText size={48} style={{ opacity: 0.4 }} />
            <Typography variant="h6" sx={{ mt: 1 }}>
              {t('NoDocuments') ?? 'No documents found'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Pagination */}

      {applications?.totalItems < 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            px: 1,
          }}
        >
          {/* Rows per page (optional) */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {t('RowsPerPage')}
            </Typography>

            <FormControl size="small">
              <Select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                sx={{ minWidth: 90 }}
              >
                {[5, 10, 20].map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Pagination
            page={page}
            count={pageCount}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            disabled={isLoading || pageCount <= 1}
          />
        </Box>
      ) : null}
    </div>
  );
}

export default OutgoingDocumentPage;
